import { useState } from "react";
import {
  TAccessBlock,
  TAccessTarget,
  useBlockApplicationAccess,
  useUnblockApplicationAccess,
} from "../../hooks/application-access.hooks";
import { Dialog } from "../CommonComponents/Dialog/Dialog";
import { SuccessToast, WarningToast } from "../toastMessage";
import style from "./ApplicationAccess.module.css";
import { daysToUntil, todayForBlock, untilToDays } from "./blockDates";

const REASON_MAX_LENGTH = 500;

type Props = TAccessTarget & {
  /** The block that applies today, or null when there is none. */
  block: TAccessBlock | null;
  /** Who or what is being blocked, shown in the dialog. */
  targetLabel: string;
};

/**
 * The on/off switch for one block, with the dialog that asks how long and
 * why. It knows only "a scope and a target", so the same switch serves a user
 * type row, a user row, and any screen that later shows one applicant.
 */
export default function ApplicationAccessToggle({
  scope,
  target,
  block,
  targetLabel,
}: Props) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [until, setUntil] = useState("");
  const [reason, setReason] = useState("");
  const { mutate: blockAccess, isPending: blocking } =
    useBlockApplicationAccess();
  const { mutate: unblockAccess, isPending: unblocking } =
    useUnblockApplicationAccess();

  const isBlocked = !!block;
  const busy = blocking || unblocking;
  const days = until ? String(untilToDays(until)) : "";

  const closeDialog = () => {
    setDialogOpen(false);
    setUntil("");
    setReason("");
  };

  const onToggle = () => {
    if (isBlocked) {
      /*
       * Before: the text was fixed, so it said "Unblocked successfully" even
       * when the user stayed blocked through the user type.
       *
       * unblockAccess(
       *   { scope, target },
       *   { onSuccess: () => SuccessToast("Unblocked successfully") },
       * );
       */
      unblockAccess(
        { scope, target },
        {
          onSuccess: ({ data }) =>
            data.stillBlocked
              ? WarningToast(data.message)
              : SuccessToast(data.message),
        },
      );
      return;
    }
    setDialogOpen(true);
  };

  const onDaysChange = (value: string) => {
    const typedDays = Number.parseInt(value, 10);
    setUntil(typedDays > 0 ? daysToUntil(typedDays) : "");
  };

  const onBlock = () =>
    blockAccess(
      { scope, target, until: until || null, reason: reason.trim() },
      {
        onSuccess: () => {
          SuccessToast("Blocked successfully");
          closeDialog();
        },
      },
    );

  return (
    <>
      <label className={style.toggleBtn}>
        <input
          type="checkbox"
          className={style.inputCheckbox}
          checked={isBlocked}
          disabled={busy}
          onChange={onToggle}
        />
        <span className={style.slider}></span>
      </label>

      {/*
        The shared Dialog draws on the page body, outside the table cell this
        switch sits in, so the cell's clipping and text styles do not reach
        the dialog's fields and buttons.
      */}
      <Dialog open={dialogOpen} onClose={closeDialog} className={style.modal}>
        <h5 className={style.modelHeading}>Block Application Access</h5>
        <p className={style.modalTarget}>{targetLabel}</p>
        <hr className="mt-2" />

        <div className={style.fieldRow}>
          <div className={style.field}>
            <label className={style.boldLable}>Number of Days</label>
            <input
              type="number"
              min={1}
              placeholder="Enter Days"
              className={style.inputBox}
              value={days}
              onChange={(e) => onDaysChange(e.target.value)}
            />
          </div>
          <div className={style.field}>
            <label className={style.boldLable}>Blocked Until Date</label>
            <input
              type="date"
              min={todayForBlock()}
              className={style.inputBox}
              value={until}
              onChange={(e) => setUntil(e.target.value)}
            />
          </div>
        </div>

        <div className={style.fieldRow}>
          <div className={style.field}>
            <label className={style.boldLable}>Reason (optional)</label>
            <input
              type="text"
              maxLength={REASON_MAX_LENGTH}
              placeholder="Enter Reason Here"
              className={style.inputBox}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>

        <p className={style.helpText}>
          Enter the days or pick the date; one fills the other. Leave both empty
          to block until you switch it off.
        </p>

        <div className={style.buttonSection}>
          <button
            type="button"
            className={style.blockButton}
            disabled={busy}
            onClick={onBlock}
          >
            Block
          </button>
          <button
            type="button"
            className={style.closeButton}
            onClick={closeDialog}
          >
            Close
          </button>
        </div>
      </Dialog>
    </>
  );
}
